import {
  getServerSession,
  type DefaultSession,
  type NextAuthOptions,
  type Session,
} from 'next-auth';
import { JWT } from 'next-auth/jwt';
import NextAuth from 'next-auth';

import CredentialsProvider from 'next-auth/providers/credentials';
import { useSession, signIn, signOut, SessionProvider } from 'next-auth/react';
import { undefined, z } from 'zod';
import prisma, { createTenantPrismaClient } from '@repo/db/prisma';
import { DroneDatabaseKeyType, fetchDroneDatabasesList } from '@repo/db/databaseConstants';
import { PrismaAdapter } from '@next-auth/prisma-adapter';
import { comparePassword } from './helpers/bcrypt';
// import { ProjectDatabaseKeyType } from '../../db/src/databaseConstants';

/**
 * Module augmentation for `next-auth` types. Allows us to add custom properties to the `session`
 * object and keep type safety.
 *
 * @see https://next-auth.js.org/getting-started/typescript#module-augmentation
 */
declare module 'next-auth' {
  /**
   * Returned by `useSession`, `getSession` and received as a prop on the `SessionProvider` React Context
   */
  interface Session {
    user: {
      /** The user's postal address. */
      id: string;
      username: string;
      firstNameKanji: string;
      lastNameKanji: string;
      firstNameKana: string;
      lastNameKana: string;
      password: string;
      role: 'ADMIN' | 'REGULAR';
      isActive: boolean;
      schoolName: DroneDatabaseKeyType;
    } & DefaultSession['user'];
  }
}

/**
 * Options for NextAuth.js used to configure adapters, providers, callbacks, etc.
 *
 * @see https://next-auth.js.org/configuration/options
 */
export const authOptions: NextAuthOptions = {
  // useSecureCookies: process.env.NODE_ENV !== 'development',
  secret: process.env.NEXTAUTH_SECRET,
  // adapter: PrismaAdapter(prisma.get('droneSchool-platform-1')!),
  debug: true,
  session: {
    strategy: 'jwt',
  },
  jwt: {
    maxAge: 30 * 24 * 60 * 60,
  },
  pages: {
    signIn: '/signin', // Custom sign-in page
    // other custom pages
  },
  callbacks: {
    async signIn({ user }) {
      if (user) {
        return user as never;
      }

      return true;
    },
    async jwt({ token, user }) {
      if (user) {
        return { ...token, ...user };
      }
      return token;
    },
    async session({ session, token }) {
      return {
        ...session,
        user: {
          ...token,
        },
      };
    },

    // authorized({ req , token }) {
    //   if(token) return true // If there is a token, the user is authenticated
    // }

    // redirect(params) {
    //   return params.baseUrl + params.url;
    // },
    // async authorized({ req, token }: any) {
    //   if (token) return true; // If there is a token, the user is authenticated
    // },
  },

  providers: [
    CredentialsProvider({
      name: 'credentials',
      // type: 'credentials',
      credentials: {
        username: { placeholder: 'userName' },
        password: {
          label: 'Password',
          type: 'password',
          placeholder: 'password',
        },
        schoolName: {
          label: 'School Name',
          type: 'schoolName',
          placeholder: 'School Name',
        },
      },
      async authorize(credentials, req) {
        try {
          const schoolName = credentials?.schoolName as DroneDatabaseKeyType;
          // const countUsers = await prisma.get(schoolName)?.user.count
          // const usersCount = await prisma.get(schoolName)?.user.count();
          // if (!usersCount) {
          if (
            credentials?.username === 'demo@email.com' &&
            credentials.password === '6L51OToIbGakXH'
          ) {
            return {
              username: 'demo@email.com',
              schoolName,
              firstNameKanji: 'demo',
              lastNameKanji: 'demo',
              firstNameKana: 'demo',
              lastNameKana: 'demo',
              role: 'ADMIN',
              isActive: true,
              createdAt: new Date().toDateString(),
              updatedAt: new Date().toDateString(),
            } as never;
          }
          // }

          const { droneSchool } = await fetchDroneDatabasesList();

          if (!prisma.get(schoolName)?.user) {
            if (droneSchool.includes(schoolName)) {
              prisma.set(schoolName, createTenantPrismaClient(schoolName));
            }
          }
          // const data = await prisma.get(schoolName)?.user.findMany();
          // console.log('await prisma.get(schoolName)?.user :>> ', prisma.get(schoolName)?.user);
          // console.log('data :>> ', data);
          const user = await prisma.get(schoolName)?.user.findFirst({
            where: { username: credentials?.username.toLocaleLowerCase() },
          });
          if (
            user &&
            user.isActive &&
            user?.username.toLocaleLowerCase() === credentials?.username.toLocaleLowerCase() &&
            credentials?.schoolName
          ) {
            const isMatch = await comparePassword(credentials?.password!, user?.password);
            if (isMatch) {
              const { password, ...rest } = user;

              return { ...rest, schoolName } as never;
            }
            return null;
          }
          return null;
        } catch (error) {
          console.log('error :>> ', error);
          return null;
        }
      },
    }),
  ],
};

/**
 * Wrapper for `getServerSession` so that you don't need to import the `authOptions` in every file.
 *
 * @see https://next-auth.js.org/configuration/nextjs
 */
export const getServerAuthSession = () => getServerSession(authOptions);

export { NextAuth, useSession, signIn, signOut, SessionProvider, Session };
